
import { EnhancedFoodItem, ClientProfile } from './types';
import { FoodItem } from '@/types/meal-plan';

// Convert enhanced meal to basic FoodItem with reasoning
export const enhancedToBasic = (enhancedMeal: EnhancedFoodItem & { score?: number }): FoodItem => {
  // Generate contextual reasoning
  let reasoning = '';
  
  if (enhancedMeal.clientFrequency && enhancedMeal.clientFrequency >= 8) {
    reasoning += 'Frequently chosen by you. ';
  } else if (enhancedMeal.similarProfileFrequency && enhancedMeal.similarProfileFrequency >= 8) {
    reasoning += 'Popular among similar profiles. ';
  }
  
  if (enhancedMeal.reasoningKeywords.length > 0) {
    reasoning += `Rich in ${enhancedMeal.reasoningKeywords.slice(0, 2).join(' and ')}. `;
  }
  
  if (enhancedMeal.protein >= 15) {
    reasoning += 'High protein content for muscle maintenance. ';
  }
  
  if (enhancedMeal.dietaryTags.includes('high-fiber')) {
    reasoning += 'Good source of fiber for digestive health. ';
  }
  
  if (enhancedMeal.healthGoalTags.length > 0) {
    reasoning += `Supports ${enhancedMeal.healthGoalTags[0].replace('-', ' ')}. `;
  }
  
  if (!reasoning) {
    reasoning = `${enhancedMeal.regionCuisine} cuisine with balanced nutrition.`;
  }

  return {
    id: enhancedMeal.id,
    name: enhancedMeal.name,
    calories: enhancedMeal.calories,
    protein: enhancedMeal.protein,
    carbs: enhancedMeal.carbs,
    fat: enhancedMeal.fat,
    micronutrients: enhancedMeal.micronutrients,
    serving: enhancedMeal.serving,
    // Store reasoning in a way that can be accessed by the UI
    reasoning: reasoning.trim()
  } as FoodItem & { reasoning: string };
};

// Scoring algorithm for ranking meals
export const scoreMeals = (meals: EnhancedFoodItem[], clientProfile: ClientProfile) => {
  return meals.map(meal => {
    let score = 0;
    
    // Client frequency score (0-30 points)
    if (meal.clientFrequency) {
      score += meal.clientFrequency * 3;
    }
    
    // Similar profile frequency score (0-20 points)
    if (meal.similarProfileFrequency) {
      score += meal.similarProfileFrequency * 2;
    }
    
    // Health goals alignment (0-15 points)
    const matchingGoals = meal.healthGoalTags.filter(tag => 
      clientProfile.healthGoals.includes(tag)
    ).length;
    score += matchingGoals * 5;
    
    // Regional preference bonus (0-10 points)
    if (meal.regionCuisine.toLowerCase() === clientProfile.regionPreference.toLowerCase()) {
      score += 10;
    }
    
    // Activity level alignment (0-5 points)
    if (clientProfile.activityLevel === 'active' && meal.dietaryTags.includes('high-protein')) {
      score += 5;
    }
    
    return { ...meal, score };
  });
};

// Smart filtering algorithm
export const filterMeals = (meals: EnhancedFoodItem[], clientProfile: ClientProfile, region?: string) => {
  return meals.filter(meal => {
    // Primary filter: Dietary preferences
    if (clientProfile.dietaryPreference === 'vegetarian' && !meal.dietaryTags.includes('vegetarian')) {
      return false;
    }
    if (clientProfile.dietaryPreference === 'vegan' && !meal.dietaryTags.includes('vegetarian')) {
      return false;
    }
    
    // Secondary filter: Food restrictions
    for (const restriction of clientProfile.restrictions) {
      if (meal.dietaryTags.some(tag => tag.includes(restriction) || restriction.includes(tag))) {
        return false;
      }
      if (meal.name.toLowerCase().includes(restriction)) {
        return false;
      }
    }
    
    // Tertiary filter: Regional preference (if specified)
    if (region && meal.regionCuisine.toLowerCase() !== region.toLowerCase()) {
      return false;
    }
    
    return true;
  });
};
